import React from 'react';
import { useAccount } from '../contexts/AccountContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useTheme } from '../contexts/ThemeContext';

const HeaderMiddlePanel: React.FC = () => {
  const { accountBalance } = useAccount();
  const { isDemoAccount, toggleAccountType } = useAccountType();
  const { theme } = useTheme();

  return (
    <div className="flex-1 flex items-center justify-between px-2 min-w-0">
      {/* Balance Information - Responsive */}
      <div className="flex items-center space-x-2 lg:space-x-4 xl:space-x-6 text-xs min-w-0 flex-shrink">
        {/* Main Balance - Always visible */}
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs lg:text-xs">{isDemoAccount ? 'Demo Balance:' : 'Account Balance:'}</span>
          <span className="font-medium theme-text-primary text-xs lg:text-xs">
            ${isDemoAccount ? accountBalance.demoBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : accountBalance.liveBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
        
        {/* Equity - Hidden on small screens */}
        <div className="flex items-center space-x-1 lg:space-x-2 lg:flex hidden">
          <span className="theme-text-quaternary text-xs">Equity:</span>
          <span className="font-medium theme-text-primary text-xs">
            ${accountBalance.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
        
        {/* Margin - Hidden on small/medium screens */}
        <div className="flex items-center space-x-1 lg:space-x-2 xl:flex hidden">
          <span className="theme-text-quaternary text-xs">Margin:</span>
          <span className="theme-text-primary font-medium text-xs">
            ${accountBalance.margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
        
        {/* Margin Level - Hidden on small/medium screens */}
        <div className="flex items-center space-x-1 lg:space-x-2 xl:flex hidden">
          <span className="theme-text-quaternary text-xs">Margin Level:</span>
          <span className="theme-text-primary font-medium text-xs">
            {accountBalance.marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
          </span>
        </div>
        
        {/* Profit - Always visible but can shrink */}
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs">Profit:</span>
          <span className={`font-medium text-xs ${accountBalance.profit >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
            {accountBalance.profit >= 0 ? '+' : ''}${accountBalance.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
      </div>

      {/* Account Type Toggle - Responsive */}
      <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
        <span className="theme-text-quaternary text-xs lg:text-xs">Account:</span>
        <div className="flex items-center space-x-1 lg:space-x-2">
          <span className={`text-xs font-medium transition-colors duration-200 ${isDemoAccount ? 'text-[#2AB7A6]' : 'theme-text-quaternary'}`}>
            Demo
          </span>
          <button
            onClick={toggleAccountType}
            className={`relative inline-flex h-3 w-6 lg:h-4 lg:w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
              isDemoAccount 
                ? theme === 'light' ? 'bg-gray-300' : 'bg-gray-500'
                : 'bg-[#2AB7A6]'
            }`}
          >
            <span
              className={`inline-block h-2 w-2 lg:h-3 lg:w-3 transform rounded-full bg-white transition-transform duration-200 ${
                isDemoAccount ? 'translate-x-0.5' : 'translate-x-3 lg:translate-x-4'
              }`}
            />
          </button>
          <span className={`text-xs font-medium transition-colors duration-200 ${!isDemoAccount ? 'text-[#2AB7A6]' : 'theme-text-quaternary'}`}>
            Live
          </span>
        </div>
      </div>
    </div>
  );
};

export default HeaderMiddlePanel;
